
import React from 'react';
import { Check, Trash, UserPlus, UserX, UserCheck, Share2, Gift, Handshake, Sparkles, Camera, CheckCircle, XCircle, Eye, Trophy, PartyPopper, Store } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

// Define TypeScript interface for notification content
interface NotificationContent {
  status?: string;
  sender_id?: string;
  connection_id?: string;
  sender_name?: string;
  receiver_name?: string;
  pledge_id?: string;
  from_user_id?: string;
  custom_reward_details?: string;
  message?: string;
  goal_id?: string;
  todo_id?: string;
  suggestion_id?: string;
  suggestions?: Array<{ goal_id: string; suggested_category: string }>;
  [key: string]: any;
}

export interface Notification {
  id: string;
  type: string;
  content: string | NotificationContent;
  is_read: boolean;
  created_at: string;
  goal_id?: string;
  [key: string]: any;
}

interface NotificationItemProps {
  notification: Notification;
  markAsRead: (id: string) => void;
  deleteNotification: (id: string) => void;
  handleAcceptConnection: (connectionId: string, notificationId: string) => void;
  handleRejectConnection: (connectionId: string, notificationId: string) => void;
  handleAcceptRewardPledge?: (pledgeId: string, notificationId: string) => void;
  handleFulfillPledge?: (pledgeId: string, notificationId: string) => void;
  getNotificationDescription: (notification: Notification) => string;
  onNavigate?: (path: string) => void;
}

const getIconConfig = (type: string, content?: string | NotificationContent) => {
  switch (type) {
    case 'goal_shared':
      return { icon: Share2, bg: 'bg-primary/10', color: 'text-primary' };
    case 'connection_request':
      return { icon: UserPlus, bg: 'bg-primary/10', color: 'text-primary' };
    case 'reward_pledge_received':
      return { icon: Gift, bg: 'bg-green-500/10', color: 'text-green-500' };
    case 'reward_pledge_accepted':
      return { icon: Handshake, bg: 'bg-blue-500/10', color: 'text-blue-500' };
    case 'category_suggestions':
      return { icon: Sparkles, bg: 'bg-violet-500/10', color: 'text-violet-500' };
    case 'proof_submitted':
      return { icon: Camera, bg: 'bg-amber-500/10', color: 'text-amber-500' };
    case 'proof_approved':
      return { icon: CheckCircle, bg: 'bg-green-500/10', color: 'text-green-500' };
    case 'proof_rejected':
      return { icon: XCircle, bg: 'bg-red-500/10', color: 'text-red-500' };
    case 'pledge_goal_completed':
      return { icon: Trophy, bg: 'bg-amber-500/10', color: 'text-amber-500' };
    case 'pledge_fulfilled':
      return { icon: PartyPopper, bg: 'bg-green-500/10', color: 'text-green-500' };
    case 'place_claim_submitted':
      return { icon: Store, bg: 'bg-sky-500/10', color: 'text-sky-600' };
    case 'tastemaker_contribution_submitted':
      return { icon: Store, bg: 'bg-violet-500/10', color: 'text-violet-600' };

    case 'connection_response': {
      try {
        let parsed: NotificationContent;
        if (typeof content === 'string') parsed = JSON.parse(content);
        else parsed = content as NotificationContent;
        if (parsed?.status === 'accepted') return { icon: UserCheck, bg: 'bg-green-500/10', color: 'text-green-500' };
        if (parsed?.status === 'rejected') return { icon: UserX, bg: 'bg-red-500/10', color: 'text-red-500' };
      } catch {}
      return { icon: UserPlus, bg: 'bg-primary/10', color: 'text-primary' };
    }
    default:
      return { icon: Share2, bg: 'bg-primary/10', color: 'text-primary' };
  }
};

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  markAsRead,
  deleteNotification,
  handleAcceptConnection,
  handleRejectConnection,
  handleAcceptRewardPledge,
  handleFulfillPledge,
  getNotificationDescription,
  onNavigate,
}) => {
  const navigate = useNavigate();
  const { icon: Icon, bg, color } = getIconConfig(notification.type, notification.content);

  const navigateSafely = (path: string) => {
    markAsRead(notification.id);
    if (onNavigate) {
      onNavigate(path);
      return;
    }
    navigate(path);
  };

  const renderNotificationActions = () => {
    if (notification.type === 'connection_request') {
      let connectionId;
      try {
        if (typeof notification.content === 'string') {
          connectionId = JSON.parse(notification.content).connection_id;
        } else if (notification.content && typeof notification.content === 'object') {
          connectionId = (notification.content as NotificationContent).connection_id;
        }
        if (!connectionId) return null;
      } catch { return null; }
      
      return (
        <div className="flex gap-2 mt-2.5">
          <Button variant="default" size="sm" className="min-h-10 sm:min-h-7 text-xs rounded-full px-3" onClick={() => handleAcceptConnection(connectionId, notification.id)}>
            Accept
          </Button>
          <Button variant="outline" size="sm" className="min-h-10 sm:min-h-7 text-xs rounded-full px-3 text-destructive hover:text-destructive" onClick={() => handleRejectConnection(connectionId, notification.id)}>
            Decline
          </Button>
        </div>
      );
    }

    if (notification.type === 'reward_pledge_received' && handleAcceptRewardPledge) {
      let pledgeId;
      try {
        if (typeof notification.content === 'string') {
          pledgeId = JSON.parse(notification.content).pledge_id;
        } else if (notification.content && typeof notification.content === 'object') {
          pledgeId = (notification.content as NotificationContent).pledge_id;
        }
        if (!pledgeId) return null;
      } catch { return null; }
      
      return (
        <div className="flex gap-2 mt-2.5">
          <Button variant="default" size="sm" className="min-h-10 sm:min-h-7 text-xs rounded-full px-3" onClick={() => handleAcceptRewardPledge(pledgeId, notification.id)}>
            <Handshake className="mr-1 h-3 w-3" />
            Accept Deal
          </Button>
        </div>
      );
    }

    if (notification.type === 'pledge_goal_completed' && handleFulfillPledge) {
      let pledgeId;
      try {
        if (typeof notification.content === 'string') {
          pledgeId = JSON.parse(notification.content).pledge_id;
        } else if (notification.content && typeof notification.content === 'object') {
          pledgeId = (notification.content as NotificationContent).pledge_id;
        }
        if (!pledgeId) return null;
      } catch { return null; }

      return (
        <div className="flex gap-2 mt-2.5">
          <Button variant="default" size="sm" className="min-h-10 sm:min-h-7 text-xs rounded-full px-3" onClick={() => handleFulfillPledge(pledgeId, notification.id)}>
            <Gift className="mr-1 h-3 w-3" />
            Fulfill Reward
          </Button>
          <Button variant="outline" size="sm" className="min-h-10 sm:min-h-7 text-xs rounded-full px-3" onClick={() => navigateSafely('/connections')}>
            <Eye className="mr-1 h-3 w-3" />
            View Details
          </Button>
        </div>
      );
    }

    if (notification.type === 'category_suggestions') {
      return (
        <div className="flex gap-2 mt-2.5">
          <Button variant="outline" size="sm" className="min-h-10 sm:min-h-7 text-xs rounded-full px-3 bg-violet-500/10 hover:bg-violet-500/20 text-violet-600 dark:text-violet-400 border-violet-200 dark:border-violet-800" onClick={() => navigateSafely('/dashboard?filter=ai-suggestions')}>
            <Eye className="mr-1 h-3 w-3" />
            Review
          </Button>
        </div>
      );
    }
    
    return null;
  };

  return (
    <div className={`group relative mx-2 mb-1.5 rounded-lg transition-all duration-200 hover:bg-accent/60 ${
      !notification.is_read 
        ? 'bg-accent/30 border-l-[3px] border-l-primary' 
        : 'border-l-[3px] border-l-transparent'
    }`}>
      <div className="flex items-start gap-3 p-3">
        {/* Icon */}
        <div className={`flex-shrink-0 w-8 h-8 rounded-full ${bg} flex items-center justify-center mt-0.5`}>
          <Icon className={`h-4 w-4 ${color}`} />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <p className={`text-sm leading-snug ${!notification.is_read ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
            {getNotificationDescription(notification)}
          </p>
          <p className="text-xs text-muted-foreground/70 mt-1">
            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
          </p>
          {renderNotificationActions()}
        </div>

        {/* Unread dot */}
        {!notification.is_read && (
          <div className="flex-shrink-0 w-2 h-2 rounded-full bg-primary mt-2" />
        )}
      </div>

      {/* Hover actions */}
      <div className="absolute top-2 right-2 flex gap-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-opacity">
        {!notification.is_read && (
          <Button variant="ghost" size="icon" className="h-9 w-9 sm:h-6 sm:w-6 rounded-full hover:bg-background" onClick={() => markAsRead(notification.id)}>
            <Check className="h-3 w-3" />
          </Button>
        )}
        <Button variant="ghost" size="icon" className="h-9 w-9 sm:h-6 sm:w-6 rounded-full text-muted-foreground hover:text-destructive hover:bg-background" onClick={() => deleteNotification(notification.id)}>
          <Trash className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
};
